/**
 * RawMdxBlock / RawInline — passthrough nodes that keep MDX the editor does not
 * model (Tabs, CodeGroup, Figure, Video, unknown components, raw HTML, comments,
 * inline <appname> style tags) EXACTLY as written, so opening + saving a page in
 * Visual mode never drops or mangles content.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Trash2, Code2 } from 'lucide-react';
import { useEditorTheme } from '../EditorThemeContext';

const tagOf = (source) => {
  const m = /^<\s*(!--|[A-Za-z][\w.:-]*)/.exec(source || '');
  if (!m) return 'MDX';
  return m[1] === '!--' ? 'Comment' : m[1];
};

const RawMdxView = ({ node, updateAttributes, deleteNode }) => {
  const isLight = useEditorTheme() === 'light';
  const source = node.attrs.source || '';
  const tag = tagOf(source);
  const rows = Math.min(28, Math.max(2, source.split('\n').length));
  return (
    <NodeViewWrapper className="raw-mdx-wrapper my-4 group/raw" data-testid="raw-mdx-block" data-tag={tag} contentEditable={false}>
      <div className={`rounded-lg border overflow-hidden ${isLight ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-[#111]'}`}>
        <div className={`flex items-center gap-2 px-3 py-1.5 text-[11px] border-b ${isLight ? 'border-gray-200 text-gray-500' : 'border-white/10 text-slate-400'}`}>
          <Code2 className="w-3.5 h-3.5" />
          <span className="font-medium">MDX block</span>
          <span className="font-mono text-[#00A1B2]">&lt;{tag}&gt;</span>
          <span className="opacity-70">kept exactly as written &mdash; edit the source below</span>
          <button
            type="button"
            onClick={() => deleteNode()}
            className="ml-auto p-1 rounded opacity-0 group-hover/raw:opacity-100 text-gray-400 hover:text-red-400"
            title="Delete block"
            data-testid="raw-mdx-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <textarea
          className={`raw-mdx-textarea w-full px-3 py-2 font-mono text-[12.5px] leading-5 bg-transparent outline-none resize-y ${isLight ? 'text-gray-800' : 'text-slate-200'}`}
          value={source}
          rows={rows}
          spellCheck={false}
          onChange={(e) => updateAttributes({ source: e.target.value })}
          data-testid="raw-mdx-source"
        />
      </div>
    </NodeViewWrapper>
  );
};

export const RawMdxBlock = Node.create({
  name: 'rawMdx',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      source: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-source') || '',
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="raw-mdx"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'raw-mdx', 'data-source': node.attrs.source })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(RawMdxView, {
      stopEvent: ({ event }) => event.target?.tagName === 'TEXTAREA' || event.type === 'mousedown' || event.type === 'click',
    });
  },

  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          const src = (node.attrs.source || '').replace(/\s+$/, '');
          if (src) state.write(src + '\n');
          state.closeBlock(node);
        },
      },
    };
  },
});

export const RawInline = Node.create({
  name: 'rawInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      source: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-source') || '',
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="raw-inline"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'raw-inline',
        'data-source': node.attrs.source,
        class: 'raw-inline-chip',
        title: 'Raw MDX/HTML kept as written',
      }),
      node.attrs.source,
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          state.write(node.attrs.source || '');
        },
      },
    };
  },
});
